import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrateBadges } from "../lib/gamification";
import { GraduationCap, Loader2, Star } from "lucide-react";

const MODES = [
  { id: "plan", label: "Plan an essay (no draft yet)" },
  { id: "essay", label: "Score my SAT/AP draft" },
];

export default function SATPrep() {
  const [mode, setMode] = useState("plan");
  const [prompt, setPrompt] = useState("");
  const [writing, setWriting] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!prompt.trim()) { toast.error("Add a prompt first."); return; }
    if (mode === "essay" && !writing.trim()) { toast.error("Paste your draft to be scored."); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/sat/essay-prep", { prompt, mode, student_writing: writing });
      setResult(data);
      celebrateBadges(data._meta);
    } catch { toast.error("Couldn't get prep feedback."); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center"><GraduationCap size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>SAT/AP Writing Prep</h1>
        </div>
        <p className="text-slate-600">Plan a response or get rubric-aligned feedback (Reading · Analysis · Writing 1-4 each).</p>
      </div>

      <div className="tactile-card p-6 sm:p-8 space-y-4">
        <div>
          <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Mode</div>
          <div className="flex gap-2 flex-wrap">
            {MODES.map(m => (
              <button key={m.id} data-testid={`sat-mode-${m.id}`} onClick={()=>setMode(m.id)}
                className={`rounded-full px-4 py-2 text-sm font-bold border-2 ${mode===m.id ? 'bg-amber-400 border-amber-500 text-slate-900':'bg-white border-slate-200 text-slate-700 hover:border-amber-300'}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase font-bold tracking-widest text-slate-500">SAT/AP-style prompt</label>
          <textarea data-testid="sat-prompt" value={prompt} onChange={(e)=>setPrompt(e.target.value)} rows={3}
            placeholder="Paste an SAT or AP-style prompt here…"
            className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 px-4 py-3 outline-none resize-none" />
        </div>
        {mode === "essay" && (
          <div>
            <label className="text-xs uppercase font-bold tracking-widest text-slate-500">Your essay draft</label>
            <textarea data-testid="sat-writing" value={writing} onChange={(e)=>setWriting(e.target.value)} rows={10}
              className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-amber-400 px-4 py-3 outline-none resize-none" />
          </div>
        )}
        <button data-testid="sat-submit" onClick={submit} disabled={loading}
          className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-6 py-3 tactile-shadow-amber inline-flex items-center gap-2 disabled:opacity-60">
          {loading ? <><Loader2 className="animate-spin" size={14}/> Coaching…</> : (mode === "plan" ? "Build my plan" : "Score my draft")}
        </button>
      </div>

      {result && mode === "plan" && (
        <div data-testid="plan-result" className="space-y-4 fade-up">
          <Block title="Strategy" body={result.strategy} color="bg-indigo-50" />
          <ListBlock title="Thesis options" items={result.thesis_options} color="bg-amber-50" />
          <div className="tactile-card p-6">
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-3">Evidence buckets</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(result.evidence_buckets || []).map((b, i) => (
                <div key={i} className="rounded-2xl bg-rose-50 border border-rose-100 p-4">
                  <div className="font-bold text-slate-900 mb-2" style={{fontFamily:'Outfit'}}>{b.theme}</div>
                  <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                    {(b.sources || []).map((s, j) => <li key={j}>{s}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <ListBlock title="Rhetorical moves" items={result.rhetorical_moves} color="bg-violet-50" />
          <ListBlock title="Common pitfalls" items={result.common_pitfalls} color="bg-rose-50" />
          <Block title="5-minute outline checklist" body={result.five_minute_outline} color="bg-emerald-50" />
        </div>
      )}

      {result && mode === "essay" && (
        <div data-testid="score-result" className="space-y-4 fade-up">
          <div className="grid grid-cols-3 gap-3">
            {["reading", "analysis", "writing"].map((k) => {
              const v = result.scores?.[k] ?? 0;
              return (
                <div key={k} className="tactile-card p-5 text-center">
                  <div className="text-xs uppercase font-bold tracking-widest text-slate-500 capitalize">{k}</div>
                  <div className="text-3xl font-bold mt-2" style={{fontFamily:'Outfit'}}>{v}<span className="text-base text-slate-400">/4</span></div>
                  <div className="flex justify-center gap-0.5 mt-2">
                    {[1,2,3,4].map(n=>(<Star key={n} size={14} className={n<=v?'fill-amber-400 text-amber-400':'text-slate-300'} />))}
                  </div>
                </div>
              );
            })}
          </div>
          <Block title="Thesis assessment" body={result.thesis_assessment} color="bg-indigo-50" />
          <Block title="Evidence assessment" body={result.evidence_assessment} color="bg-amber-50" />
          <Block title="Analysis assessment" body={result.analysis_assessment} color="bg-rose-50" />
          <Block title="Top revision" body={result.top_revision} color="bg-emerald-50" highlight />
          <ListBlock title="SAT-specific tips" items={result.sat_tips} color="bg-violet-50" />
        </div>
      )}
    </div>
  );
}

const Block = ({ title, body, color, highlight }) => (
  <div className={`tactile-card p-6 ${color} ${highlight ? 'border-emerald-200' : ''}`}>
    <div className="text-xs uppercase font-bold tracking-widest text-slate-600 mb-2">{title}</div>
    <p className={`text-slate-900 ${highlight ? 'font-semibold text-lg' : ''}`}>{body}</p>
  </div>
);
const ListBlock = ({ title, items, color }) => (
  <div className={`tactile-card p-6 ${color}`}>
    <div className="text-xs uppercase font-bold tracking-widest text-slate-600 mb-2">{title}</div>
    <ul className="list-disc pl-5 space-y-1 text-slate-800">
      {(items || []).map((it,i)=>(<li key={i}>{it}</li>))}
    </ul>
  </div>
);
